import { v4 as uuidv4 } from "uuid";
import { BaklavaEvent, SequentialHook } from "@baklavajs/events";
import { Graph } from "./graph";
import { mapValues } from "./utils";
import { getGraphNodeTypeString } from "./graphNode";
export class GraphTemplate {
    /** Create a new GraphTemplate from the nodes and connections inside the graph instance */
    static fromGraph(graph, editor) {
        return new GraphTemplate(graph.save(), editor);
    }
    /** Get the name of the graph template */
    get name() {
        return this._name;
    }
    /** Set the name of the graph template */
    set name(v) {
        this._name = v;
        this.events.nameChanged.emit(v);
        const nt = this.editor.nodeTypes.get(getGraphNodeTypeString(this));
        if (nt) {
            nt.title = v;
        }
    }
    constructor(state, editor) {
        /** Graph template id */
        this.id = uuidv4();
        this._name = "Subgraph";
        this.events = {
            nameChanged: new BaklavaEvent(this),
            updated: new BaklavaEvent(this),
        };
        this.hooks = {
            beforeLoad: new SequentialHook(this),
            afterSave: new SequentialHook(this),
        };
        this.editor = editor;
        if (state.id) {
            this.id = state.id;
        }
        if (state.name) {
            this._name = state.name;
        }
        this.update(state);
    }
    /** Update the state of the graph template with the provided state */
    update(state) {
        this.nodes = state.nodes;
        this.connections = state.connections;
        this.inputs = state.inputs;
        this.outputs = state.outputs;
        this.events.updated.emit();
    }
    save() {
        return {
            id: this.id,
            name: this.name,
            nodes: this.nodes,
            connections: this.connections,
            inputs: this.inputs,
            outputs: this.outputs,
        };
    }
    /**
     * Create a new graph instance from this template
     * or load the state into the provided graph instance.
     */
    createGraph(graph) {
        const idMap = new Map();
        const createNewId = (oldId) => {
            const newId = uuidv4();
            idMap.set(oldId, newId);
            return newId;
        };
        const getNewId = (oldId) => {
            const newId = idMap.get(oldId);
            if (!newId) {
                throw new Error(`Unable to create graph from template: Could not map old id ${oldId} to new id`);
            }
            return newId;
        };
        const mapNodeInterfaceIds = (interfaceStates) => {
            return mapValues(interfaceStates, (intf) => {
                const clonedIntf = {
                    id: createNewId(intf.id),
                    templateId: intf.id,
                    value: intf.value,
                };
                return clonedIntf;
            });
        };
        const nodes = this.nodes.map((n) => ({
            ...n,
            id: createNewId(n.id),
            inputs: mapNodeInterfaceIds(n.inputs),
            outputs: mapNodeInterfaceIds(n.outputs),
        }));
        const connections = this.connections.map((c) => ({
            id: createNewId(c.id),
            from: getNewId(c.from),
            to: getNewId(c.to),
        }));
        const inputs = this.inputs.map((i) => ({
            id: i.id,
            name: i.name,
            nodeInterfaceId: getNewId(i.nodeInterfaceId),
        }));
        const outputs = this.outputs.map((o) => ({
            id: o.id,
            name: o.name,
            nodeInterfaceId: getNewId(o.nodeInterfaceId),
        }));
        const clonedState = {
            id: uuidv4(),
            nodes,
            connections,
            inputs,
            outputs,
        };
        if (!graph) {
            graph = new Graph(this.editor);
        }
        graph.load(clonedState);
        graph.template = this;
        return graph;
    }
}
